import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Section } from "@/components/PageHeader";
import { ScrollText } from "lucide-react";

const ACTION_LABEL = {
  "user.created": "created user",
  "user.deleted": "removed user",
  "user.invited": "sent invite",
  "user.role_changed": "changed role",
  "project.deleted": "deleted project",
  "project.archived": "archived project",
  "project.unarchived": "unarchived project",
  "project.billing_updated": "updated billing",
  "tasks.bulk_reassigned": "bulk-reassigned tasks",
};

function describeMeta(action, meta) {
  if (!meta) return "";
  if (action === "user.role_changed") return `${meta.from} → ${meta.to}`;
  if (action === "project.billing_updated") return `${meta.project}: $${meta.from} → $${meta.to}`;
  if (action === "tasks.bulk_reassigned") return `${meta.count} tasks → ${meta.to_name || meta.to}`;
  if (action === "user.created" || action === "user.invited") return `${meta.email} (${meta.role})`;
  if (action === "user.deleted") return `${meta.email}`;
  if (action === "project.deleted" || action === "project.archived" || action === "project.unarchived") return meta.project || "";
  return JSON.stringify(meta);
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/audit-logs")
      .then(r => setLogs(r.data))
      .catch(e => setError(e.response?.data?.detail || "Cannot load audit log"));
  }, []);

  return (
    <div data-testid="audit-page">
      <PageHeader
        eyebrow="Compliance"
        title="Audit log"
        description="A time-stamped record of every consequential action — role changes, billing updates, archives, and reassignments."
        testid="audit-header"
      />
      <Section>
        {error ? (
          <div className="border border-red-200 bg-red-50 text-sm text-red-700 p-4 rounded-sm" data-testid="audit-error">{error}</div>
        ) : logs.length === 0 ? (
          <div className="border border-dashed border-neutral-300 p-16 grid place-items-center text-center" data-testid="audit-empty">
            <ScrollText size={32} className="text-neutral-400 mb-3" />
            <div className="font-display text-xl font-bold mb-1">No audit entries yet</div>
            <div className="text-sm text-neutral-500">Once admins start changing roles, billing, or archiving projects, the history shows up here.</div>
          </div>
        ) : (
          <div className="border border-neutral-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50/50 text-[10px] uppercase tracking-widest text-neutral-500">
                  <th className="text-left px-4 py-3 font-semibold">When</th>
                  <th className="text-left px-4 py-3 font-semibold">Actor</th>
                  <th className="text-left px-4 py-3 font-semibold">Action</th>
                  <th className="text-left px-4 py-3 font-semibold">Detail</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l.id} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`audit-row-${l.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-neutral-600">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{l.actor_name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{l.actor_role}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs px-2 py-0.5 bg-neutral-900 text-white rounded-sm font-medium">{ACTION_LABEL[l.action] || l.action}</span>
                    </td>
                    <td className="px-4 py-3 text-neutral-600 text-xs">{describeMeta(l.action, l.meta)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
}
